import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil, Plus, Trash2, WalletCards } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { Checkbox } from '../components/ui/Checkbox';
import { EmptyState } from '../components/ui/EmptyState';
import { Switch } from '../components/ui/Switch';
import { api } from '../lib/api';
import { apiErrorMessage, useToast } from '../lib/ToastContext';
import { Loader } from '../components/ui/Spinner';

type LeaveType = { id: string; name: string; code: string; yearlyEntitlement: string; allowCarryForward: boolean; maxCarryForward: string; isPaid: boolean; isActive: boolean };

function LeaveTypeForm({ initial, onSubmit, onCancel, pending }: { initial?: LeaveType; onSubmit: (v: Record<string, unknown>) => void; onCancel: () => void; pending: boolean }) {
  const [name, setName] = useState(initial?.name ?? '');
  const [code, setCode] = useState(initial?.code ?? '');
  const [yearlyEntitlement, setYearlyEntitlement] = useState(initial?.yearlyEntitlement ?? '');
  const [allowCarryForward, setAllowCarryForward] = useState(initial?.allowCarryForward ?? false);
  const [maxCarryForward, setMaxCarryForward] = useState(initial?.maxCarryForward ?? '0');
  const [isPaid, setIsPaid] = useState(initial?.isPaid ?? true);
  const [isActive, setIsActive] = useState(initial?.isActive ?? true);

  function submit(e: FormEvent) {
    e.preventDefault();
    onSubmit({ name, code: code.toUpperCase(), yearlyEntitlement, allowCarryForward, maxCarryForward: allowCarryForward ? maxCarryForward : '0', isPaid, isActive });
  }

  return (
    <form className="card mb-6 grid gap-4 sm:grid-cols-2" onSubmit={submit}>
      <label className="label">Leave type name<input className="field mt-1" required placeholder="e.g. Casual Leave" value={name} onChange={(e) => setName(e.target.value)} /></label>
      <label className="label">Short code<input className="field mt-1" required maxLength={20} placeholder="e.g. CL" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} /></label>
      <label className="label">Yearly entitlement (days)<input type="number" min="0" step="0.5" className="field mt-1" required value={yearlyEntitlement} onChange={(e) => setYearlyEntitlement(e.target.value)} /></label>
      <div className="flex items-end"><Checkbox checked={isPaid} onChange={setIsPaid} label="Paid leave" /></div>
      <div className="flex items-center"><Checkbox checked={allowCarryForward} onChange={setAllowCarryForward} label="Allow carry-forward to next year" /></div>
      {allowCarryForward && <label className="label">Max days to carry forward<input type="number" min="0" step="0.5" className="field mt-1" value={maxCarryForward} onChange={(e) => setMaxCarryForward(e.target.value)} /></label>}
      {initial && <label className="label flex items-center gap-3 sm:col-span-2">Active<Switch checked={isActive} onChange={setIsActive} /></label>}
      <div className="flex items-end gap-2 sm:col-span-2">
        <button className="btn-primary" disabled={pending}>Save</button>
        <button type="button" className="btn-secondary" onClick={onCancel}>Cancel</button>
      </div>
    </form>
  );
}

export function LeaveTypesPage() {
  const client = useQueryClient();
  const { success, error: toastError } = useToast();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<LeaveType | null>(null);
  const leaveTypes = useQuery({ queryKey: ['leave-types'], queryFn: async () => (await api.get<LeaveType[]>('/api/leave-types')).data });

  const create = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.post('/api/leave-types', body),
    onSuccess: () => { void client.invalidateQueries({ queryKey: ['leave-types'] }); success('Leave type created'); setOpen(false); },
    onError: (err) => toastError(apiErrorMessage(err, 'Could not create leave type')),
  });
  const update = useMutation({
    mutationFn: ({ id, ...body }: Record<string, unknown> & { id: string }) => api.patch(`/api/leave-types/${id}`, body),
    onSuccess: () => { void client.invalidateQueries({ queryKey: ['leave-types'] }); success('Leave type updated'); setEditing(null); },
    onError: (err) => toastError(apiErrorMessage(err, 'Could not update leave type')),
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/api/leave-types/${id}`),
    onSuccess: () => { void client.invalidateQueries({ queryKey: ['leave-types'] }); success('Leave type deleted'); },
    onError: (err) => toastError(apiErrorMessage(err, 'Could not delete — leave balances or applications may still reference it')),
  });

  return (
    <>
      <div className="mb-6 flex items-start justify-between gap-3">
        <div><h1 className="text-3xl font-bold">Leave types</h1><p className="mt-1 text-muted">Casual, sick, unpaid — and their yearly entitlement.</p></div>
        <button className="btn-primary" onClick={() => { setOpen(!open); setEditing(null); }}><Plus size={18} /> New</button>
      </div>

      {open && <LeaveTypeForm onSubmit={(v) => create.mutate(v)} onCancel={() => setOpen(false)} pending={create.isPending} />}

      {leaveTypes.isLoading && <Loader />}
      {!leaveTypes.isLoading && leaveTypes.data?.length === 0 && <EmptyState title="No leave types yet" />}
      <div className="space-y-3">
        {leaveTypes.data?.map((type) =>
          editing?.id === type.id ? (
            <div className="card" key={type.id}><LeaveTypeForm initial={type} onSubmit={(v) => update.mutate({ id: type.id, ...v })} onCancel={() => setEditing(null)} pending={update.isPending} /></div>
          ) : (
            <article key={type.id} className="card flex items-center justify-between gap-3">
              <div className="flex min-w-0 items-center gap-3">
                <WalletCards size={18} className="shrink-0 text-muted" />
                <div className="min-w-0">
                  <strong className={type.isActive ? '' : 'text-muted line-through'}>{type.name}</strong>
                  <p className="mt-0.5 text-sm text-muted">{type.code} • {type.yearlyEntitlement} days/yr{!type.isPaid && ' • unpaid'}{type.allowCarryForward && ` • carries forward up to ${type.maxCarryForward}`}</p>
                </div>
              </div>
              <div className="flex shrink-0 gap-2">
                <button className="btn-secondary !px-3" aria-label="Edit" onClick={() => { setEditing(type); setOpen(false); }}><Pencil size={16} /></button>
                <button className="btn-secondary !px-3" aria-label="Delete" onClick={() => { if (confirm(`Delete "${type.name}"?`)) remove.mutate(type.id); }}><Trash2 size={16} /></button>
              </div>
            </article>
          ),
        )}
      </div>
    </>
  );
}
